import {
    isPartnerCockpitSubscriptionEnded,
    type PartnerCockpitSubscriptionCharge,
} from '@houzy/schemas/partner-cockpit/subscription';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useId, useState, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { apiErrorMessageKey } from '../../../lib/api';
import { houzyContact } from '../../../lib/houzyContact';
import { SettingsQueryGate } from '../../settings/components/SettingsQueryGate';
import {
    subscriptionQueryKey,
    useBillingPortalMutation,
    useCancelSubscriptionMutation,
    useRevokeCancellationMutation,
    useSubscription,
} from '../api/subscriptionApi';
import { BillingTable } from '../components/BillingTable';
import { CancelSubscriptionDialog } from '../components/CancelSubscriptionDialog';
import { InvoiceDetailDialog } from '../components/InvoiceDetailDialog';
import { ScheduledCancellationPanel } from '../components/ScheduledCancellationPanel';
import { SubscriptionFeatures } from '../components/SubscriptionFeatures';
import { SubscriptionPlanCard } from '../components/SubscriptionPlanCard';
import { SubscriptionUpgradeOffer } from '../components/SubscriptionUpgradeOffer';
import { UnverifiedAlert } from '../components/UnverifiedAlert';
import { subscriptionBillingHintKey } from '../labels';

export function SubscriptionSettingsPage(): ReactElement {
    const { t } = useTranslation();
    const headingId = useId();
    const queryClient = useQueryClient();
    const query = useSubscription();
    const cancel = useCancelSubscriptionMutation();
    const revoke = useRevokeCancellationMutation();
    const billingPortal = useBillingPortalMutation();

    const [selectedCharge, setSelectedCharge] = useState<PartnerCockpitSubscriptionCharge | null>(null);
    const [isCancelOpen, setIsCancelOpen] = useState(false);

    const navigate = useNavigate();
    const { returned } = useSearch({ from: '/_authenticated/settings/subscription' });
    useEffect(() => {
        if (returned === 'billing') {
            void queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
            void navigate({ to: '.', search: {}, replace: true });
        }
    }, [returned, queryClient, navigate]);

    return (
        <Card variant="outlined" component="section" aria-labelledby={headingId}>
            <CardHeader
                title={t('SETTINGS.SUBSCRIPTION.TITLE')}
                slotProps={{ title: { variant: 'h6', component: 'h2', id: headingId } }}
            />
            <CardContent sx={{ pt: { xs: 2, md: 0 } }}>
                <SettingsQueryGate
                    query={query}
                    loading={
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    }>
                    {({ subscription }) => {
                        if (!subscription) {
                            return <SubscriptionUpgradeOffer />;
                        }

                        const hasEnded = isPartnerCockpitSubscriptionEnded(subscription);

                        return (
                            <Stack spacing={3} sx={hasEnded ? { opacity: 0.6 } : undefined}>
                                {!subscription.isValidated && !hasEnded && (
                                    <UnverifiedAlert paymentMethod={subscription.paymentMethod} />
                                )}

                                <ScheduledCancellationPanel
                                    subscription={subscription}
                                    isSubmitting={revoke.isPending}
                                    errorMessage={
                                        revoke.error
                                            ? t(apiErrorMessageKey(revoke.error, 'SETTINGS.SUBSCRIPTION.ERRORS.REVOKE'))
                                            : undefined
                                    }
                                    onRevoke={() => revoke.mutate()}
                                />

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <SubscriptionPlanCard subscription={subscription} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <SubscriptionFeatures />
                                    </Box>
                                </Stack>

                                <Divider />

                                <Stack spacing={2}>
                                    <Typography variant="subtitle1">
                                        {t('SETTINGS.SUBSCRIPTION.BILLING.TITLE')}
                                    </Typography>
                                    <BillingTable charges={subscription.charges} onSelect={setSelectedCharge} />
                                </Stack>

                                {!subscription.cancelEffectiveAt && !hasEnded && (
                                    <Stack spacing={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            <Trans
                                                i18nKey={subscriptionBillingHintKey(
                                                    subscription.paymentMethod,
                                                    subscription.canManageBilling,
                                                )}
                                                components={{
                                                    supportLink: (
                                                        <Link
                                                            href={`mailto:${houzyContact.supportEmail}`}
                                                            underline="hover"
                                                        />
                                                    ),
                                                }}
                                            />
                                        </Typography>
                                        <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            spacing={2}
                                            sx={{ alignItems: { md: 'flex-start' } }}>
                                            <Button
                                                variant="outlined"
                                                disabled={!subscription.canManageBilling || billingPortal.isPending}
                                                onClick={() =>
                                                    billingPortal.mutate(undefined, {
                                                        onSuccess: ({ redirectUrl }) => {
                                                            window.location.assign(redirectUrl);
                                                        },
                                                    })
                                                }>
                                                {t('SETTINGS.SUBSCRIPTION.BILLING.MANAGE')}
                                            </Button>
                                            <Tooltip
                                                title={
                                                    subscription.canCancel
                                                        ? ''
                                                        : t('SETTINGS.SUBSCRIPTION.CANCEL.UNAVAILABLE')
                                                }>
                                                <Box component="span" sx={{ display: 'flex' }}>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        disabled={!subscription.canCancel}
                                                        onClick={() => setIsCancelOpen(true)}
                                                        sx={{ flexGrow: 1 }}>
                                                        {t('SETTINGS.SUBSCRIPTION.CANCEL.ACTION')}
                                                    </Button>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                )}

                                <CancelSubscriptionDialog
                                    subscription={subscription}
                                    open={isCancelOpen}
                                    isSubmitting={cancel.isPending}
                                    errorMessage={
                                        cancel.error
                                            ? t(apiErrorMessageKey(cancel.error, 'SETTINGS.SUBSCRIPTION.ERRORS.CANCEL'))
                                            : undefined
                                    }
                                    onConfirm={(comment) =>
                                        cancel.mutate(comment ? { comment } : {}, {
                                            onSuccess: () => setIsCancelOpen(false),
                                        })
                                    }
                                    onCancel={() => setIsCancelOpen(false)}
                                />
                                <InvoiceDetailDialog charge={selectedCharge} onClose={() => setSelectedCharge(null)} />
                            </Stack>
                        );
                    }}
                </SettingsQueryGate>
            </CardContent>
        </Card>
    );
}
